import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { keyframes } from "@emotion/react";

import imagen1 from "../images/imagen1.webp";
import imagen2 from "../images/imagen2.webp";
import imagen3 from "../images/imagen3.webp";
import imagen4 from "../images/imagen4.webp";
import imagen5 from "../images/imagen5.webp";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Hero1() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  // ✅ Carrusel de fotos (independiente)
  const images = useMemo(() => [imagen1, imagen2, imagen3, imagen4, imagen5], []);
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % images.length);
    }, 5500);
    return () => clearInterval(t);
  }, [images.length]);

  // ✅ Carrusel de frases (independiente)
  const frases = useMemo(
    () => [
      "Vive la magia de Pichilemu frente al mar",
      "Descanso cómodo a pasos de la playa",
      "Tu experiencia comienza en Hostal Bichon",
    ],
    [],
  );
  const [currentText, setCurrentText] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setCurrentText((prev) => (prev + 1) % frases.length);
    }, 3000);
    return () => clearInterval(t);
  }, [frases.length]);

  // ✅ Animación gradiente del botón
  const gradientShift = useMemo(
    () => keyframes`
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    `,
    [],
  );

  // ✅ CTA Reservar: radius 1 + shadow suave SIEMPRE
  const reserveSx = useMemo(
    () => ({
      fontSize: { xs: "1.02rem", sm: "1.08rem", md: "1.1rem" }, // un toque más grande
      borderRadius: 2, // 👈 lo que pediste
      px: { xs: 3, sm: 3.6 },
      py: { xs: 1.35, sm: 1.5 },
      fontWeight: 600,
      textTransform: "none",
      color: "#fff",

      // 👇 sombra suave incluso sin hover
      boxShadow: "0 10px 24px rgba(0,0,0,0.22)",

      background: "linear-gradient(270deg, #2DB193, #16a357, #c72e64)",
      backgroundSize: "600% 600%",
      animation: `${gradientShift} 3.2s ease infinite`,

      transition:
        "transform 220ms ease, box-shadow 220ms ease, filter 220ms ease",
      "&:hover": {
        transform: "translateY(-1px)",
        boxShadow: "0 14px 34px rgba(0,0,0,0.28)",
        filter: "brightness(1.05)",
      },
    }),
    [gradientShift],
  );

  const verMasSx = {
    fontSize: { xs: "1rem", sm: "1.05rem" },
    fontWeight: 500,
    textTransform: "none",
    color: "#fff",
    px: 0,
    py: 0.5,
    minWidth: "auto",
    backgroundColor: "transparent",
    position: "relative",
    transition: "transform 220ms ease, opacity 220ms ease",
    "&:hover": {
      backgroundColor: "transparent",
      transform: "translateY(-1px)",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      left: 0,
      right: 0,
      bottom: -6,
      height: 2,
      backgroundColor: "currentColor",
      transform: "scaleX(0)",
      transformOrigin: "left",
      transition: "transform 240ms ease",
      opacity: 0.85,
    },
    "&:hover::after": {
      transform: "scaleX(1)",
    },
  };

  return (
    <Box
      component="section"
      sx={{
        scrollSnapAlign: "start",
        height: { xs: "70vh", sm: "80vh", md: "80vh" },
        minHeight: { xs: 520, sm: 600, md: "unset" },
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {/* ✅ Carrusel de imágenes (crossfade) */}
      {images.map((img, idx) => (
        <Box
          key={img}
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${img})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: idx === currentImage ? 1 : 0,
            transition: "opacity 900ms ease",
            transform: idx === currentImage ? "scale(1.02)" : "scale(1.01)",
          }}
        />
      ))}

      {/* Capa oscura */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 55%, rgba(0,0,0,0.55) 100%)",
          zIndex: 1,
        }}
      />

      {/* ✅ Contenido */}
      <Box
        sx={{
          zIndex: 2,
          textAlign: "center",
          maxWidth: "1000px",
        }}
      >
        {/* Frase: más estética y más chica en laptop */}
        <Typography
          component="h1"
          sx={{
            fontSize: {
              xs: "1.9rem",
              sm: "2.2rem",
              md: "2.4rem", // 👈 antes era 3.5rem en laptop; ahora más fino
              lg: "2.9rem",
            },
            fontFamily: '"DynaPuff", cursive',
            fontWeight: 500,
            color: "#fff",
            textShadow: "2px 2px 10px rgba(0,0,0,0.75)",
            lineHeight: 1.12,
          }}
        >
          {frases[currentText]}
        </Typography>

        {/* CTAs */}
        <Box
          sx={{
            mt: { xs: 3, sm: 3.5 },
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 2,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            sx={reserveSx}
            onClick={() => navigate("/reserve")}
          >
            Reservar ahora
          </Button>

          <Button
            variant="text"
            sx={verMasSx}
            onClick={() => scrollToId("instalaciones")} // cambiá el id si querés
          >
            Ver más
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
